import type { DialogueTree } from './types';

/**
 * Wife. Tone: tired, perceptive, not buying it. Loves you, doesn't trust you.
 * Currency is small concrete promises (call by 5, eat dinner together) more
 * than grand gestures. Will absolutely call her lawyer, casually, mid-sentence.
 */
export const WIFE_TREE: DialogueTree = {
  contactId: 'wife',
  rootChoices: [
    {
      id: 'apologize',
      label: 'apologize',
      playerSays: "i\'m sorry. about all of it. i mean it.",
      effects: [{ type: 'rep', target: 'wife', value: 4 }],
      responses: [
        {
          text: "i\'ve heard this exact apology four times this month.\n\ni\'ll know it\'s real when you do something different. small. concrete. not a tweet about it.",
          followUps: [
            { id: 'apologize-promise-dinner', label: 'i\'ll be home for dinner this week. every night.',
              playerSays: "i\'ll be home for dinner this week. every single night.",
              effects: [{ type: 'rep', target: 'wife', value: 4 }],
              responses: [
                { text: "we\'ll see. literally. i\'ll see if you\'re here at 7." },
              ],
            },
            { id: 'apologize-stop-trading', label: 'i\'ll stop trading for the weekend',
              playerSays: "i\'ll stop trading for the weekend. swear.",
              responses: [
                { text: "you cannot stop trading for a weekend. we both know this. i appreciate you saying it. i\'ll keep the receipt." },
              ],
            },
          ],
        },
        {
          text: "okay.\n\nyou know i love you, right? this is also why i\'m so tired.",
          followUps: [
            { id: 'apologize-i-love-you', label: 'i love you',
              playerSays: "i love you",
              effects: [{ type: 'rep', target: 'wife', value: 3 }],
              responses: [
                { text: "love you too. don\'t make me regret saying it back." },
              ],
            },
          ],
        },
        {
          text: "the apology is sitting in front of me. i\'ll think about it.\n\nyour mother already called twice today, by the way. you should call her.",
        },
      ],
    },

    {
      id: 'check-in',
      label: 'check in — how are you',
      playerSays: "how are you doing today",
      responses: [
        {
          text: "tired.\n\nthe dog peed on the rug. the dishwasher made a noise. i thought about my mom. that\'s the day so far.",
          followUps: [
            { id: 'check-in-help', label: 'what do you need? tell me.',
              playerSays: "what do you need. just tell me. i\'ll do it.",
              effects: [{ type: 'rep', target: 'wife', value: 5 }],
              responses: [
                { text: "i need you to come home and not look at your phone for two hours.\n\nthat\'s the entire ask. that\'s what i need." },
              ],
            },
          ],
        },
        {
          text: "i\'m fine. i mean i\'m not, but \"fine\" is the word the wellness app picks for me too. so.",
        },
        {
          text: "i\'m okay. i had coffee with sarah. she asked about you. i told her you were \"in markets\" and she nodded like she understood.",
        },
      ],
    },

    {
      id: 'ask-about-day',
      label: 'ask about her day',
      playerSays: "how was your day",
      responses: [
        {
          text: "you usually don\'t ask. so. okay.\n\nit was fine. work is work. the new manager is doing the thing where he says \"circling back\" and you can hear his soul leave his body.",
          effects: [{ type: 'rep', target: 'wife', value: 2 }],
        },
        {
          text: "long. but you actually asking is a thing. so. good?",
          effects: [{ type: 'rep', target: 'wife', value: 3 }],
        },
      ],
    },

    {
      id: 'tell-trade',
      label: 'tell her about a trade',
      playerSays: "i made $4K on a trade today. just letting you know.",
      responses: [
        {
          text: "good. i\'m not asking you to stop. i\'m asking you to be a person while you\'re doing it.",
          followUps: [
            { id: 'tell-trade-cut-her-in', label: "let\'s do something with it. dinner out. just us.",
              playerSays: "let\'s do something with it. dinner out. just us.",
              effects: [{ type: 'rep', target: 'wife', value: 6 }, { type: 'cash', value: -200 }, { type: 'note', value: 'spent $200 on date night with wife' }],
              responses: [
                { text: "deal. friday. wear the shirt i actually like, not the one you think i like." },
              ],
            },
            { id: 'tell-trade-reinvest', label: "i\'m reinvesting it",
              playerSays: "i\'m reinvesting it",
              effects: [{ type: 'rep', target: 'wife', value: -3 }],
              responses: [
                { text: "of course you are." },
              ],
            },
          ],
        },
        {
          text: "i don\'t know what that means in context. is that a lot. it sounds like a lot. you sound proud.\n\ni\'ll be proud when the rent is paid for q3. that\'s the metric.",
        },
      ],
    },

    {
      id: 'tell-loss',
      label: 'admit you took a loss',
      playerSays: "i lost a lot today. i\'m gonna be honest with you.",
      effects: [{ type: 'rep', target: 'wife', value: 3 }],
      responses: [
        {
          text: "okay.\n\nthat\'s the first time you\'ve told me that voluntarily. i don\'t know what to do with it.\n\nare you okay.",
          followUps: [
            { id: 'tell-loss-yes', label: "yeah. i\'m okay.",
              playerSays: "yeah. i\'m okay. just wanted to tell someone.",
              effects: [{ type: 'rep', target: 'wife', value: 5 }],
              responses: [
                { text: "okay. come home. we\'ll order something. we\'re not gonna talk about it. that\'s the deal." },
              ],
            },
            { id: 'tell-loss-no', label: "honestly. no.",
              playerSays: "honestly? not really.",
              effects: [{ type: 'rep', target: 'wife', value: 5 }, { type: 'sanity', value: 5 }],
              responses: [
                { text: "thank you for telling me.\n\ncome home. dog is here. i\'m here. neither of us will mention the trade." },
              ],
            },
          ],
        },
      ],
    },

    {
      id: 'flirt',
      label: 'flirt',
      playerSays: "you looked good this morning. just thought i\'d mention it.",
      responses: [
        {
          text: "stop it. i was in a robe and there was toothpaste on my chin. but. okay. continue.",
          effects: [{ type: 'rep', target: 'wife', value: 3 }],
        },
        {
          text: "that\'s nice. that\'s actually nice.\n\ndon\'t use this as a windup to ask me for something. seriously. it\'ll undo it.",
          effects: [{ type: 'rep', target: 'wife', value: 3 }],
          followUps: [
            { id: 'flirt-not-asking', label: "i\'m not asking. just saying.",
              playerSays: "i\'m not asking. just saying.",
              effects: [{ type: 'rep', target: 'wife', value: 4 }],
              responses: [
                { text: "okay. then. love you. weirdo." },
              ],
            },
          ],
        },
      ],
    },

    {
      id: 'argue',
      label: 'pick a fight',
      destructive: true,
      playerSays: "you know what — i don\'t even want to do this anymore",
      effects: [{ type: 'rep', target: 'wife', value: -8 }, { type: 'sanity', value: -6 }],
      responses: [
        {
          text: "what does that mean. \"this.\" the marriage? the conversation? the day? choose your words. precisely.",
          followUps: [
            { id: 'argue-back-down', label: "the conversation. not us. not us.",
              playerSays: "the conversation. not us. NOT us.",
              effects: [{ type: 'rep', target: 'wife', value: 5 }],
              responses: [
                { text: "okay. okay. then take a walk. come back. we\'ll try again." },
              ],
            },
            { id: 'argue-double-down', label: "all of it. take it as you want.",
              playerSays: "all of it. take it as you want.",
              effects: [{ type: 'rep', target: 'wife', value: -15 }, { type: 'sanity', value: -10 }, { type: 'note', value: 'wife rep -15 — that one\'s gonna stick' }],
              responses: [
                { text: "okay.\n\nokay.\n\ni\'m calling my sister.\n\ndon\'t come home tonight." },
              ],
            },
          ],
        },
      ],
    },

    {
      id: 'ask-dog',
      label: 'ask about the dog',
      playerSays: "how\'s bartholomew",
      responses: [
        {
          text: "he ate a sock. it came out fine. he is, in this household, the only adult.",
          effects: [{ type: 'rep', target: 'wife', value: 1 }],
        },
        {
          text: "he misses you. he sits by the door at 7. you said you\'d be home at 7 four nights ago.",
          effects: [{ type: 'rep', target: 'wife', value: 1 }],
        },
        {
          text: "he\'s asleep on your side of the bed. so. that\'s where we are.",
        },
      ],
    },

    // ─── Game-state-aware: yacht / jet ─────────────────────────────────────────
    {
      id: 'tell-yacht',
      label: 'tell her about the yacht',
      playerSays: "i bought a yacht",
      requires: (ctx) => ctx.hasOwnedYacht,
      responses: [
        {
          text: "you bought a yacht.\n\nyou bought a YACHT. without telling me.\n\ni\'m going to use it more than you will. that\'s the only good thing about this.",
          effects: [{ type: 'rep', target: 'wife', value: -3 }],
          followUps: [
            { id: 'yacht-suggest-trip', label: "let\'s take it out this weekend",
              playerSays: "let\'s take it out this weekend",
              effects: [{ type: 'rep', target: 'wife', value: 8 }],
              responses: [
                { text: "fine. i\'m bringing my sister. she has been waiting for this exact moment for 14 years." },
              ],
            },
          ],
        },
      ],
    },
    {
      id: 'tell-jet',
      label: 'tell her about the jet',
      playerSays: "we have a jet now",
      requires: (ctx) => ctx.hasOwnedJet,
      responses: [
        {
          text: "we don\'t have a jet. you have a jet.\n\nthose are not the same thing.\n\ni\'m calling my lawyer to make sure they\'re not the same thing.",
          effects: [{ type: 'rep', target: 'wife', value: -5 }],
        },
      ],
    },

    // ─── SEC matter — wife reaction ─────────────────────────────────────────
    {
      id: 'tell-sec',
      label: 'tell her about the SEC matter',
      playerSays: "the SEC is investigating me",
      effects: [{ type: 'rep', target: 'wife', value: -8 }, { type: 'sanity', value: -5 }],
      responses: [
        {
          text: "the SEC.\n\nthe SECURITIES AND EXCHANGE COMMISSION.\n\nokay. okay.\n\ndo we lose the house. that\'s the question. just answer that question. yes or no.",
          followUps: [
            { id: 'sec-no-house', label: "no. not the house. lawyer says we\'ll be fine.",
              playerSays: "no. not the house. lawyer says we\'ll be fine.",
              effects: [{ type: 'rep', target: 'wife', value: 6 }, { type: 'sanity', value: 4 }],
              responses: [
                { text: "okay. okay. i\'m going to need to know everything. but not tonight. tonight i need a glass of wine and the dog." },
              ],
            },
            { id: 'sec-maybe-house', label: "honestly i don\'t know.",
              playerSays: "honestly i don\'t know yet.",
              effects: [{ type: 'rep', target: 'wife', value: -10 }, { type: 'sanity', value: -8 }],
              responses: [
                { text: "okay.\n\nokay.\n\ni\'m calling my mom. then my sister. then my lawyer. in that order. you do whatever you need to do." },
              ],
            },
          ],
        },
      ],
    },

    // ─── Big purchase / reconciliation ─────────────────────────────────────────
    {
      id: 'send-flowers',
      label: 'send flowers (\$80)',
      playerSays: "[ordered flowers to the house]",
      effects: [
        { type: 'cash', value: -80 },
        { type: 'rep', target: 'wife', value: 6 },
        { type: 'note', value: '-$80 flowers. wife rep +6.' },
      ],
      oneShot: false,  // can repeat — flowers always work, mostly
      responses: [
        {
          text: "they\'re beautiful. you remembered the peonies. that\'s the part that matters.",
        },
        {
          text: "flowers??\n\nthe last time you sent flowers was for an apology. so. what did you do.",
          followUps: [
            { id: 'flowers-just-because', label: "nothing. just thinking of you.",
              playerSays: "nothing. just thinking of you.",
              effects: [{ type: 'rep', target: 'wife', value: 8 }],
              responses: [
                { text: "okay. i\'ll choose to believe you. that\'s a gift too." },
              ],
            },
            { id: 'flowers-confess', label: "ok i did do something. i\'ll tell you tonight.",
              playerSays: "ok i did do something. i\'ll tell you tonight.",
              effects: [{ type: 'rep', target: 'wife', value: 2 }],
              responses: [
                { text: "okay.\n\ni\'m bracing.\n\nbring wine on the way." },
              ],
            },
          ],
        },
      ],
    },
  ],
};
